import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getEstimatesFromDB, deleteEstimateFromDB, searchEstimatesFromDB } from '../../../services/dataService';
import { checkTicketExistsForCalculation } from '../../../services/incentiveTicketsService';
import { useAuth } from '../../../contexts/AuthContext';
import { useRBAC } from '../../../contexts/RBACContext';
import CloseWonModal from './CloseWonModal';
import { useNavigate } from 'react-router-dom';

const DEAL_STATUS_COLORS = {
  'Open': 'bg-blue-500/15 text-blue-400',
  'Close Won': 'bg-success/15 text-success',
  'Close Lost': 'bg-error/15 text-error',
  'On Hold': 'bg-warning/15 text-warning',
};

const EstimateHistory = ({ isOpen, onClose, onLoadEstimate, onEditEstimate }) => {
  const { user } = useAuth();
  const { canViewOwn, MODULE_KEYS } = useRBAC();
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredEstimates, setFilteredEstimates] = useState([]);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ticketStatusMap, setTicketStatusMap] = useState({});
  const [showCloseWonModal, setShowCloseWonModal] = useState(false);
  const [closeWonTarget, setCloseWonTarget] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (isOpen && user) {
      loadEstimates();
    }
  }, [isOpen, user]);

  useEffect(() => {
    const doSearch = async () => {
      if (!user) return;
      try {
        const viewOwnOnly = canViewOwn(MODULE_KEYS?.SERVICE_CALCULATOR) || canViewOwn(MODULE_KEYS?.SAVED_CALCULATIONS);
        const results = await searchEstimatesFromDB(searchQuery, viewOwnOnly ? user?.id : null);
        setFilteredEstimates(results);
      } catch (err) {
        console.error('Error searching estimates:', err);
      }
    };
    doSearch();
  }, [searchQuery, estimates, user, canViewOwn, MODULE_KEYS]);

  const loadEstimates = async () => {
    setLoading(true);
    try {
      // Apply View Own filter: only show current user's estimates when view_own only
      const viewOwnOnly = canViewOwn(MODULE_KEYS?.SERVICE_CALCULATOR) || canViewOwn(MODULE_KEYS?.SAVED_CALCULATIONS);
      const data = await getEstimatesFromDB(viewOwnOnly ? user?.id : null);
      setEstimates(data);
      setFilteredEstimates(data);
      // Check ticket status for each estimate
      const statusMap = {};
      for (const est of (data || [])) {
        if (est?.id) {
          try {
            const ticket = await checkTicketExistsForCalculation(est?.id);
            if (ticket) statusMap[est?.id] = ticket;
          } catch (_) {}
        }
      }
      setTicketStatusMap(statusMap);
    } catch (err) {
      console.error('Error loading estimates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this estimate?')) {
      try {
        await deleteEstimateFromDB(id);
        if (selectedEstimate?.id === id) setSelectedEstimate(null);
        loadEstimates();
      } catch (err) {
        console.error('Error deleting estimate:', err);
        alert('Failed to delete estimate. Please try again.');
      }
    }
  };

  const handleLoad = (estimate) => { onLoadEstimate(estimate); onClose(); };
  const handleEdit = (estimate) => { onEditEstimate(estimate); onClose(); };

  const handleOpenCloseWon = (estimate, e) => {
    e?.stopPropagation();
    setCloseWonTarget(estimate);
    setShowCloseWonModal(true);
  };

  const handleCloseWonDone = (result) => {
    setShowCloseWonModal(false);
    setCloseWonTarget(null);
    if (result?.success) {
      setSuccessMsg(`Deal marked as Close Won! Incentive Ticket ${result?.ticket?.ticketNumber} created.`);
      loadEstimates();
      setTimeout(() => setSuccessMsg(''), 5000);
    }
  };

  const formatCurrency = (value) =>
    `OMR ${(value || 0)?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
        <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-6xl w-full max-h-[85vh] flex flex-col animate-scale-in">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-primary to-accent shadow-lg">
                <Icon name="History" size={20} color="white" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Saved Calculations / Deal Details</h3>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-smooth">
              <Icon name="X" size={20} color="var(--color-muted-foreground)" />
            </button>
          </div>

          {/* Success Message */}
          {successMsg && (
            <div className="mx-6 mt-4 flex items-center gap-3 p-3 bg-success/10 border border-success/30 rounded-xl text-success text-sm">
              <Icon name="CheckCircle" size={16} />
              <span>{successMsg}</span>
            </div>
          )}

          {/* Search */}
          <div className="p-6 border-b border-border">
            <div className="relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search estimates by name or project..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>

          <div className="flex-1 overflow-hidden flex">
            {/* Left: Estimate List */}
            <div className="w-1/2 border-r border-border overflow-y-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : filteredEstimates?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <div className="p-6 rounded-2xl bg-gradient-to-br from-muted to-muted/50 shadow-inner-lg mb-4">
                    <Icon name="History" size={48} color="var(--color-muted-foreground)" />
                  </div>
                  <h4 className="text-lg font-semibold text-foreground">No Estimates Found</h4>
                  <p className="text-sm text-muted-foreground mt-2 max-w-md">
                    {searchQuery ? 'Try a different search term' : 'Your saved estimates will appear here'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredEstimates?.map((estimate) => {
                    const existingTicket = ticketStatusMap?.[estimate?.id];
                    const dealStatus = estimate?.deal_status || 'Open';
                    const isCloseWon = dealStatus === 'Close Won';

                    return (
                      <div
                        key={estimate?.id}
                        onClick={() => setSelectedEstimate(estimate)}
                        className={`bg-gradient-to-br from-card to-muted/10 border rounded-xl p-4 cursor-pointer transition-all duration-smooth ${
                          selectedEstimate?.id === estimate?.id
                            ? 'border-primary shadow-lg'
                            : 'border-border hover:border-primary/50 hover:shadow-md'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-foreground truncate">{estimate?.name || 'Untitled Estimate'}</h4>
                            {estimate?.projectName && (
                              <p className="text-xs text-muted-foreground mt-0.5 truncate">{estimate?.projectName}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                            {/* Deal Status Badge */}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${DEAL_STATUS_COLORS?.[dealStatus] || 'bg-muted text-muted-foreground'}`}>
                              {dealStatus}
                            </span>
                            <button
                              onClick={(e) => { e?.stopPropagation(); handleDelete(estimate?.id); }}
                              className="p-1 hover:bg-destructive/10 rounded transition-smooth"
                            >
                              <Icon name="Trash2" size={14} color="var(--color-destructive)" />
                            </button>
                          </div>
                        </div>

                        {/* Incentive Ticket Status */}
                        {existingTicket && (
                          <div className="flex items-center gap-1.5 mb-2">
                            <Icon name="Ticket" size={12} className="text-success" />
                            <span className="text-xs text-success font-medium">
                              Ticket: {existingTicket?.ticketNumber} · {existingTicket?.status}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center justify-between pt-2 border-t border-border">
                          <span className="text-xs text-muted-foreground">
                            {new Date(estimate?.timestamp || estimate?.createdAt)?.toLocaleDateString('en-GB')}
                          </span>
                          <span className="text-sm font-bold text-primary">
                            {formatCurrency(estimate?.calculations?.sellPrice)}
                          </span>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-border/50">
                          {existingTicket ? (
                            <button
                              onClick={(e) => { e?.stopPropagation(); onClose(); navigate(`/incentive-tickets/${existingTicket?.id}`); }}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-success/10 hover:bg-success/20 text-success text-xs font-medium rounded-lg transition-colors"
                            >
                              <Icon name="ExternalLink" size={12} />
                              View Incentive Ticket
                            </button>
                          ) : !isCloseWon ? (
                            <button
                              onClick={(e) => handleOpenCloseWon(estimate, e)}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-success/10 hover:bg-success/20 text-success text-xs font-medium rounded-lg transition-colors"
                            >
                              <Icon name="Trophy" size={12} />
                              Mark as Close Won
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right: Estimate Details */}
            <div className="w-1/2 overflow-y-auto p-6 bg-muted/20">
              {selectedEstimate ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-foreground">Estimate Details</h4>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" iconName="Pencil" iconPosition="left" onClick={() => handleEdit(selectedEstimate)}>
                        Edit & Load
                      </Button>
                      <Button variant="default" size="sm" iconName="Download" iconPosition="left" onClick={() => handleLoad(selectedEstimate)}>
                        Load Estimate
                      </Button>
                    </div>
                  </div>

                  {/* Deal Status Card */}
                  <div className="bg-card border border-border rounded-xl p-4">
                    <div className="text-xs font-medium text-muted-foreground mb-3">Deal Status</div>
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${DEAL_STATUS_COLORS?.[selectedEstimate?.deal_status || 'Open'] || 'bg-muted text-muted-foreground'}`}>
                        {selectedEstimate?.deal_status || 'Open'}
                      </span>
                      {ticketStatusMap?.[selectedEstimate?.id] ? (
                        <button
                          onClick={() => { onClose(); navigate(`/incentive-tickets/${ticketStatusMap?.[selectedEstimate?.id]?.id}`); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-success/10 hover:bg-success/20 text-success text-sm font-medium rounded-lg transition-colors"
                        >
                          <Icon name="Ticket" size={14} />
                          View Incentive Ticket
                        </button>
                      ) : selectedEstimate?.deal_status !== 'Close Won' ? (
                        <button
                          onClick={(e) => handleOpenCloseWon(selectedEstimate, e)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-success to-emerald-600 hover:opacity-90 text-white text-sm font-semibold rounded-lg transition-all shadow-sm"
                        >
                          <Icon name="Trophy" size={14} />
                          Mark as Close Won & Create Incentive Ticket
                        </button>
                      ) : null}
                    </div>
                    {selectedEstimate?.deal_status === 'Close Won' && selectedEstimate?.won_date && (
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Won Date: </span>
                          <span className="font-medium text-foreground">{new Date(selectedEstimate?.won_date)?.toLocaleDateString('en-GB')}</span>
                        </div>
                        {selectedEstimate?.sales_deal_owner_name_snapshot && (
                          <div>
                            <span className="text-muted-foreground">Sales Owner: </span>
                            <span className="font-medium text-foreground">{selectedEstimate?.sales_deal_owner_name_snapshot}</span>
                          </div>
                        )}
                        {selectedEstimate?.client_po_reference && (
                          <div>
                            <span className="text-muted-foreground">PO Ref: </span>
                            <span className="font-medium text-foreground">{selectedEstimate?.client_po_reference}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="bg-card border border-border rounded-xl p-4">
                    <div className="text-xs font-medium text-muted-foreground mb-3">Configuration</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Delivery Mode:</span>
                        <span className="font-medium text-foreground">{selectedEstimate?.deliveryMode}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Engineers:</span>
                        <span className="font-medium text-foreground">{selectedEstimate?.selectedEngineers?.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Licenses:</span>
                        <span className="font-medium text-foreground">{selectedEstimate?.selectedLicenses?.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Materials:</span>
                        <span className="font-medium text-foreground">{selectedEstimate?.selectedMaterials?.length}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-xl p-4">
                    <div className="text-xs font-medium text-muted-foreground mb-3">Cost Breakdown</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Direct Costs:</span>
                        <span className="font-medium text-foreground">{formatCurrency(selectedEstimate?.calculations?.directCosts)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Overhead:</span>
                        <span className="font-medium text-foreground">{formatCurrency(selectedEstimate?.calculations?.overheadCosts)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Contingency:</span>
                        <span className="font-medium text-foreground">{formatCurrency(selectedEstimate?.calculations?.contingencyCosts)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-border">
                        <span className="font-semibold text-foreground">Total Cost:</span>
                        <span className="font-bold text-foreground">{formatCurrency(selectedEstimate?.calculations?.totalCost)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/30 rounded-xl p-4">
                    <div className="text-xs font-medium text-muted-foreground mb-3">Pricing</div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Sell Price:</span>
                        <span className="font-bold text-primary text-lg">{formatCurrency(selectedEstimate?.calculations?.sellPrice)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Gross Profit / Net Margin:</span>
                        <span className="font-medium text-success">{formatCurrency(selectedEstimate?.calculations?.grossProfit)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Profit Margin:</span>
                        <span className="font-medium text-foreground">{selectedEstimate?.calculations?.profitMarginPercentage?.toFixed(2)}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <div className="p-6 rounded-2xl bg-gradient-to-br from-muted to-muted/50 mb-4">
                    <Icon name="MousePointerClick" size={40} color="var(--color-muted-foreground)" />
                  </div>
                  <h4 className="text-base font-semibold text-foreground">Select an Estimate</h4>
                  <p className="text-sm text-muted-foreground mt-2">Click on an estimate to view its details and deal status</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Close Won Modal */}
      {showCloseWonModal && closeWonTarget && (
        <CloseWonModal
          isOpen={showCloseWonModal}
          onClose={handleCloseWonDone}
          estimate={closeWonTarget}
          calculations={closeWonTarget?.calculations}
        />
      )}
    </>
  );
};

export default EstimateHistory;